import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import {
  useMentorProfileReview,
  useUploadMentorImage,
} from "@/lib/api/mentor-profile";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { createCutoutBlob } from "@/lib/mentor-profile/cutout";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { isHeicFile } from "@/lib/utils/heic-converter";
import { processImage } from "@/lib/utils/image";
import { cn } from "@/lib/utils";
import { Moodies } from "@somethings-ui/core/assets";
import { Button } from "@somethings-ui/web";
import { Camera, Loader2, RotateCcw, Sparkles, Upload, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { WizardStepLayout } from "./wizard-step-layout";
import type { WizardStepProps } from "./types";

const PHOTO_EXAMPLES = [
  {
    src: "/assets/mentor-onboarding/photo-examples/aim-for-this.png",
    label: "Aim for this",
    alt: "Portrait framed from the shoulders up, face clear, plain background",
    good: true,
  },
  {
    src: "/assets/mentor-onboarding/photo-examples/too-far.png",
    label: "Too far",
    alt: "A person seated on a couch across a living room, small in the frame",
  },
  {
    src: "/assets/mentor-onboarding/photo-examples/poor-lighting.png",
    label: "Poor lighting",
    alt: "A backlit photo with the face in shadow against a bright window",
  },
  {
    src: "/assets/mentor-onboarding/photo-examples/too-close.png",
    label: "Too close",
    alt: "An extreme close-up showing only half of the face",
  },
  {
    src: "/assets/mentor-onboarding/photo-examples/too-busy.png",
    label: "Too busy",
    alt: "A portrait in front of a cluttered bookshelf and busy wall",
  },
] as const;

type Mode = "idle" | "camera" | "processing" | "denied";

/** Match-card cutout sits in a tall portrait slot — keep capture framed to this. */
const CAPTURE_ASPECT = 3 / 4;
const CAPTURE_MAX_EDGE = 1440;

function extensionForMime(mimeType: string): string {
  if (mimeType.includes("png")) return "png";
  if (mimeType.includes("webp")) return "webp";
  if (mimeType.includes("heic") || mimeType.includes("heif")) return "heic";
  return "jpg";
}

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/**
 * Cover-crop the live frame to CAPTURE_ASPECT (same as CSS object-cover on the
 * preview) and mirror horizontally so the saved photo matches the selfie view.
 */
function captureMirroredCoverFrame(video: HTMLVideoElement): Promise<Blob | null> {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return Promise.resolve(null);

  const videoAspect = vw / vh;
  let sx = 0;
  let sy = 0;
  let sw = vw;
  let sh = vh;

  if (videoAspect > CAPTURE_ASPECT) {
    sw = vh * CAPTURE_ASPECT;
    sx = (vw - sw) / 2;
  } else {
    sh = vw / CAPTURE_ASPECT;
    sy = (vh - sh) / 2;
  }

  const scale = Math.min(1, CAPTURE_MAX_EDGE / Math.max(sw, sh));
  const outW = Math.max(1, Math.round(sw * scale));
  const outH = Math.max(1, Math.round(sh * scale));

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);

  ctx.translate(outW, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/webp", 0.95);
  });
}

export function ProfilePhotoStep({
  draft,
  saveState,
  setFields,
  embedded,
  reviewerNotes,
  onContinue,
  onBack,
}: WizardStepProps) {
  const upload = useUploadMentorImage();
  const { track } = useTrackEvent();
  const { data: review } = useMentorProfileReview();
  const mentorId = review?.mentor_id;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const localUrlsRef = useRef<string[]>([]);
  const [mode, setMode] = useState<Mode>("idle");
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState<{
    profileUrl: string;
    cutoutUrl: string;
  } | null>(null);

  const previewDraft = localPreview
    ? {
        ...draft,
        profile_picture_url: localPreview.profileUrl,
        match_card_photo_url: localPreview.cutoutUrl,
      }
    : draft;

  const hasPhoto =
    !!draft.match_card_photo_url?.trim() || !!localPreview?.cutoutUrl;
  const { url: cutoutUrl } = useSignedMedia(
    localPreview?.cutoutUrl ?? draft.match_card_photo_url,
  );
  // While still showing the blob preview after upload, sign the remote URL in
  // parallel — clearing local too early blanks the UI until sign completes.
  const pendingRemoteCutout =
    localPreview &&
    draft.match_card_photo_url &&
    !draft.match_card_photo_url.startsWith("blob:")
      ? draft.match_card_photo_url
      : null;
  const { url: remoteCutoutReady } = useSignedMedia(pendingRemoteCutout);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const revokeLocalUrls = () => {
    localUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    localUrlsRef.current = [];
  };

  useEffect(() => () => {
    stopStream();
    revokeLocalUrls();
  }, []);

  useEffect(() => {
    if (mode !== "camera" || !videoRef.current || !streamRef.current) return;
    videoRef.current.srcObject = streamRef.current;
  }, [mode]);

  useEffect(() => {
    if (!localPreview || !remoteCutoutReady) return;
    setLocalPreview(null);
    revokeLocalUrls();
  }, [localPreview, remoteCutoutReady]);

  const process = async (
    blob: Blob,
    originalFilename: string,
    method: "camera" | "upload",
  ) => {
    stopStream();
    setError(null);
    // Commit + paint the spinner before WASM blocks the main thread.
    flushSync(() => setMode("processing"));
    await waitForPaint();
    const startedAt = performance.now();

    try {
      const cutoutBlob = await createCutoutBlob(blob);
      const localProfile = URL.createObjectURL(blob);
      const localCutout = URL.createObjectURL(cutoutBlob);
      revokeLocalUrls();
      localUrlsRef.current = [localProfile, localCutout];

      // Local-only preview — never flush blob: URLs into the draft API.
      setLocalPreview({ profileUrl: localProfile, cutoutUrl: localCutout });
      setMode("idle");
      setUploading(true);

      track(PostHogEvents.MENTOR_PROFILE_PHOTO_UPLOADED, {
        mentor_id: mentorId,
        method,
      });

      const [profileUrl, matchCardUrl] = await Promise.all([
        upload.mutateAsync({ blob, filename: originalFilename }),
        upload.mutateAsync({ blob: cutoutBlob, filename: "match-card.webp" }),
      ]);

      setFields({
        profile_picture_url: profileUrl,
        match_card_photo_url: matchCardUrl,
      });
      track(PostHogEvents.MENTOR_PROFILE_PHOTO_PROCESSED, {
        mentor_id: mentorId,
        processing_duration_ms: Math.round(performance.now() - startedAt),
      });
      // Keep localPreview until remoteCutoutReady effect clears it.
    } catch {
      setLocalPreview(null);
      revokeLocalUrls();
      setError("We couldn't save that photo. Please try again.");
      setMode("idle");
    } finally {
      setUploading(false);
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMode("denied");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      streamRef.current = stream;
      setMode("camera");
    } catch {
      stopStream();
      setMode("denied");
    }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    void (async () => {
      const blob = await captureMirroredCoverFrame(video);
      if (blob) await process(blob, "profile.webp", "camera");
    })();
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    if (!isHeicFile(file) && !file.type.startsWith("image/")) {
      setError("That file isn't an image. Please choose a JPG or PNG.");
      return;
    }
    void (async () => {
      try {
        const prepared = isHeicFile(file) ? await processImage(file) : file;
        await process(
          prepared,
          `profile.${extensionForMime(prepared.type || file.type)}`,
          "upload",
        );
      } catch {
        setError("We couldn't save that photo. Please try again.");
      }
    })();
  };

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      className="hidden"
      onChange={(e) => {
        onFile(e.target.files?.[0]);
        e.target.value = "";
      }}
    />
  );

  const photoExamples = (
    <div className="mb-3 space-y-2.5">
      <p className="font-body text-xs font-medium uppercase tracking-wide text-[#1f1e1d]/70">
        Examples — aim for the first one
      </p>
      <div className="grid grid-cols-5 gap-2">
        {PHOTO_EXAMPLES.map((example) => (
          <figure key={example.label} className="space-y-1">
            <img
              src={example.src}
              alt={example.alt}
              className={cn(
                "aspect-square w-full rounded-lg object-cover",
                "good" in example && example.good
                  ? "ring-2 ring-emerald-600"
                  : "ring-1 ring-red-500/40",
              )}
            />
            <figcaption
              className={cn(
                "text-center font-body text-xs",
                "good" in example && example.good
                  ? "font-medium text-emerald-700"
                  : "text-[#1f1e1d]/70",
              )}
            >
              {example.label}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );

  const renderBody = () => {
    if (mode === "processing") {
      return (
        <div className="flex flex-col items-center gap-3 rounded-[21.6px] border border-[#e7e0d5] bg-white px-6 py-12 text-center">
          <Loader2 className="size-6 animate-spin text-[#7178ff]" />
          <p className="font-body text-sm font-medium text-[#1f1e1d]">
            Lifting you out…
          </p>
          <p className="font-body text-xs text-[#797774]">
            Removing the background. This can take a moment the first time.
          </p>
        </div>
      );
    }

    if (mode === "camera") {
      return (
        <div className="space-y-4 rounded-[21.6px] border border-[#e7e0d5] bg-white p-4">
          <video
            ref={videoRef}
            muted
            autoPlay
            playsInline
            className="aspect-[3/4] w-full scale-x-[-1] rounded-[16px] bg-black object-cover"
          />
          <div className="flex items-center justify-center gap-3">
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              leadingIcon={<X className="size-4" />}
              onPress={() => {
                stopStream();
                setMode("idle");
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              fullWidth={false}
              leadingIcon={<Camera className="size-4" />}
              onPress={capture}
            >
              Capture
            </Button>
          </div>
        </div>
      );
    }

    if (mode === "denied") {
      return (
        <div className="space-y-4 rounded-[21.6px] border border-[#e7e0d5] bg-white p-5 text-center">
          <p className="font-body text-sm text-[#797774]">
            We couldn&apos;t reach your camera. Check your browser permissions,
            or upload a photo instead.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              onPress={() => setMode("idle")}
            >
              Try again
            </Button>
            <Button
              size="sm"
              fullWidth={false}
              leadingIcon={<Upload className="size-4" />}
              onPress={() => fileInputRef.current?.click()}
            >
              Upload a photo
            </Button>
          </div>
          {fileInput}
        </div>
      );
    }

    if (hasPhoto) {
      return (
        <div className="space-y-3">
          <div className="relative flex h-56 items-end justify-center overflow-hidden rounded-[21.6px] border border-[#e7e0d5] bg-[#71e0ff]/15">
            {cutoutUrl ? (
              <img
                src={cutoutUrl}
                alt="Your profile photo"
                className="h-full w-auto object-contain"
              />
            ) : (
              <Loader2 className="mb-20 size-6 animate-spin text-[#797774]" />
            )}
          </div>
          <div className="flex items-center justify-center gap-3">
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              disabled={uploading}
              leadingIcon={<RotateCcw className="size-4" />}
              onPress={() => void startCamera()}
            >
              Retake
            </Button>
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              disabled={uploading}
              leadingIcon={<Upload className="size-4" />}
              onPress={() => fileInputRef.current?.click()}
            >
              Upload another
            </Button>
          </div>
          {fileInput}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-5">
        <div className="flex flex-col items-center gap-4 rounded-[21.6px] border-2 border-dashed border-[#7178ff]/35 bg-[#7178ff]/5 px-6 py-10 text-center">
          <p className="font-body text-base font-semibold text-[#1f1e1d]">
            Add a clear photo of you
          </p>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center">
            <Button
              size="sm"
              fullWidth={false}
              leadingIcon={<Camera className="size-4" />}
              onPress={() => void startCamera()}
            >
              Take a photo
            </Button>
            <Button
              size="sm"
              variant="secondary"
              fullWidth={false}
              leadingIcon={<Upload className="size-4" />}
              onPress={() => fileInputRef.current?.click()}
            >
              Upload
            </Button>
          </div>
          {fileInput}
        </div>


      </div>
    );
  };

  return (
    <WizardStepLayout
      reviewerNotes={reviewerNotes}
      embedded={embedded}
      stepIndex={1}
      icon={
        <img src={Moodies.love} alt="" className="size-10 object-contain" />
      }
      heading="Add a profile photo"
      subheading="Snap one somewhere with a plain, light background — we'll lift you out and set you against a splash of color."
      saveState={saveState}
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!hasPhoto || mode === "processing" || uploading}
      preview={
        <MatchingProfilePreviewCard
          draft={previewDraft}
          focus={{ region: "identity", zoom: 1.05 }}
        />
      }
    >
      {mode === "idle" && photoExamples}
      {renderBody()}

      <div className="mt-1 rounded-lg bg-brand-ecru/60 px-3.5 py-3">
        <div className="flex items-center gap-1.5">
          <Sparkles className="size-3.5 text-brand-tangelo" />
          <p className="font-body text-xs font-medium text-text-primary/70">
          Tips for profile photos
          </p>
        </div>
        <div className="mt-1.5 space-y-1">

        <li key={"photo"} className="flex gap-2">
              <span className="mt-1.5 size-1 shrink-0 rounded-full bg-text-primary/40" />
              <span className="font-body text-body-3 leading-snug text-text-primary/80">
              <p className="font-body text-body-3 leading-snug text-text-primary/80">If your first photo doesn&apos;t cut out cleanly, try a different one with a simpler background.</p>

              </span>
            </li>

        </div>
      </div>

      {error && (
        <span className="font-body text-sm text-states-error">{error}</span>
      )}
    </WizardStepLayout>
  );
}
